import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { debounceTime, map, Observable, of, switchMap, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { LoadingScope } from '@ps/core-loading';
import { ToastService } from '@ps/core-overlay';
import { PsidService, SurveyService } from '@ps/survey-data-access';
import {
  ADVANCED_FILTER_TYPE,
  AdvancedFeasibilityFilters,
  DateRangeFilterParams,
  NO_PSIDS_FOUND_MESSAGE,
  RespondentFiltersRequest,
  SearchPSIDsParams,
  Survey,
} from '@ps/survey-utils';
import { DateFormatter, Nullable } from '@ps/utils';

import { selectAdvancedFeasibilityFilters } from '../audience/survey-creation-audience.selectors';
import { selectSurvey } from '../core/survey-creation-core.selectors';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

const RESOLVE_DEBOUNCE_MS = 300;

// The services default to the drawer spinner, which nothing renders on the
// launch page — the Filter Audience section shows the `content` one.
const RESOLVE_LOADING_SCOPE: LoadingScope = 'content';

/**
 * Launch-page counterpart of the audience drawer's advanced filters: a date
 * range or a survey grouping is resolved to the surveys it covers and, in
 * Include mode, to the PSIDs of those surveys. The outcome is committed as the
 * survey's `AdvancedFeasibilityFilters`, which is what the save payload reads
 * (`survey_grouping`, `ref_list`, `feasibility_exclusions`).
 */
@Injectable()
export class SurveyCreationLaunchAudienceFiltersEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);
  private readonly _toastService = inject(ToastService);

  public readonly resolveRespondentFilters$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.resolveRespondentFilters),
      debounceTime(RESOLVE_DEBOUNCE_MS),
      concatLatestFrom(() => [
        this._store.select(selectAdvancedFeasibilityFilters),
        this._store.select(selectSurvey),
      ]),
      switchMap(([request, committedFilters, survey]) =>
        this._resolvedFilters(request, committedFilters, survey).pipe(
          map((filters) =>
            SurveyCreationLaunchApiActions.resolveRespondentFiltersSuccess({
              filters,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationLaunchApiActions.resolveRespondentFiltersFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  private _resolvedFilters(
    request: RespondentFiltersRequest,
    committedFilters: AdvancedFeasibilityFilters,
    survey: Nullable<Survey>,
  ): Observable<AdvancedFeasibilityFilters> {
    const { dateRange, manualSurveyIds, includeMode } = request;

    // A date range replaces whatever grouping was typed, as the range's own
    // surveys become the grouping.
    const unresolvedFilters: AdvancedFeasibilityFilters = {
      ...committedFilters,
      type: includeMode
        ? ADVANCED_FILTER_TYPE.INCLUDE
        : ADVANCED_FILTER_TYPE.EXCLUDE,
      dateRange: this._filterDateRange(dateRange),
      matchedGroupIds: [],
      manualGroupIds: dateRange ? [] : manualSurveyIds,
      psidRefs: [],
    };

    if (!dateRange && !manualSurveyIds.length) {
      return of(unresolvedFilters);
    }

    const surveyIds$ = this._surveyIds(
      dateRange,
      manualSurveyIds,
      unresolvedFilters,
      survey,
    );

    // Exclude mode never generates PSIDs; the surveys alone are the exclusion.
    if (!includeMode) {
      return surveyIds$.pipe(
        map((surveyIds) =>
          this._filtersWithPsids(unresolvedFilters, !!dateRange, {
            surveyIds,
            refs: [],
          }),
        ),
      );
    }

    return surveyIds$.pipe(
      switchMap((surveyIds) =>
        surveyIds.length
          ? this._psidService.getAll(
              dateRange,
              surveyIds,
              RESOLVE_LOADING_SCOPE,
            )
          : of({ surveyIds: [], refs: [] }),
      ),
      tap(({ refs }) => {
        if (!refs.length) {
          this._toastService.warning(NO_PSIDS_FOUND_MESSAGE);
        }
      }),
      map((psids) =>
        this._filtersWithPsids(unresolvedFilters, !!dateRange, psids),
      ),
    );
  }

  private _surveyIds(
    dateRange: Nullable<DateRangeFilterParams>,
    manualSurveyIds: number[],
    { type, criteria }: AdvancedFeasibilityFilters,
    survey: Nullable<Survey>,
  ): Observable<number[]> {
    if (!dateRange) {
      return of(manualSurveyIds);
    }

    const locale = Option.from(survey);

    return this._surveyService
      .getSurveySummaries(
        {
          dateRange,
          countryCode: locale.mapOr('', ({ countryCode }) => countryCode),
          languageCode: locale.mapOr(
            '',
            ({ languageCode }) => languageCode ?? '',
          ),
          filterType: type,
          criteria,
        },
        RESOLVE_LOADING_SCOPE,
      )
      .pipe(map((summaries) => summaries.map(({ id }) => id)));
  }

  private _filtersWithPsids(
    unresolvedFilters: AdvancedFeasibilityFilters,
    isDateRangeLookup: boolean,
    { surveyIds, refs }: SearchPSIDsParams,
  ): AdvancedFeasibilityFilters {
    return {
      ...unresolvedFilters,
      matchedGroupIds: isDateRangeLookup ? surveyIds : [],
      manualGroupIds: isDateRangeLookup ? [] : surveyIds,
      psidRefs: refs,
    };
  }

  private _filterDateRange(
    dateRange: Nullable<DateRangeFilterParams>,
  ): AdvancedFeasibilityFilters['dateRange'] {
    return Option.from(dateRange).mapOr(
      { start: null, end: null },
      ({ start, end }) => ({
        start: DateFormatter.for(start).format(),
        end: DateFormatter.for(end).format(),
      }),
    );
  }
}
